import React, { useMemo, useRef, useState, forwardRef, useImperativeHandle, useEffect } from "react";
import { ScrollView, StyleSheet, View } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import ThemedInput from "@/components/ThemedInput";
import { EmailInput } from "@/components/ui/EmailInput";
import { PhoneInput } from "@/components/ui/PhoneInput";
import { ClientsInput } from "@/components/inputs/ClientsInput";
import { useTheme } from "@/hooks/useTheme";
import useToast from "@/hooks/useToast";
import { supabase } from "@/lib/supabase-client";

export type ContactForm = {
  first_name: string;
  last_name: string;
  email_address?: string;
  phone_number?: string;
  meta_data: {
    preferred_name?: string;
    notes?: string;
  };
  contact_clients?: {
    client: {
      id: string;
      name: string;
      status?: string;
    };
    note?: string;
  }[];
};

export type ContactPayload = { contact: ContactForm };

export interface ContactCreateFormRef {
  updateFirstName: (name: string) => void;
  updateLastName: (name: string) => void;
  handleSave: () => Promise<void>;
  handleReset: () => void;
}

type ContactCreateFormProps = {
  initialContact?: Partial<ContactForm>;
  onSave?: (payload: ContactPayload) => Promise<void> | void;
  onChange?: (payload: ContactPayload) => void;
  onDirtyChange?: (dirty: boolean) => void;
};

export const ContactCreateForm = forwardRef<ContactCreateFormRef, ContactCreateFormProps>(
  ({ initialContact, onSave, onChange, onDirtyChange }, ref) => {
    const { colors } = useTheme();
    const toast = useToast();

    const [contact, setContact] = useState<ContactForm>({
      first_name: initialContact?.first_name ?? "",
      last_name: initialContact?.last_name ?? "",
      email_address: initialContact?.email_address ?? "",
      phone_number: initialContact?.phone_number ?? "",
      meta_data: {
        preferred_name: initialContact?.meta_data?.preferred_name ?? "",
        notes: initialContact?.meta_data?.notes ?? "",
      },
      contact_clients: initialContact?.contact_clients ?? [],
    });

    const [emailError, setEmailError] = useState<string | undefined>();
    const [emailChecking, setEmailChecking] = useState(false);

    const baseline = useRef(JSON.stringify({ contact }));
    const dirty = useMemo(() => JSON.stringify({ contact }) !== baseline.current, [contact]);

    useEffect(() => {
      onChange?.({ contact });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contact]);

    // Notify parent of dirty state changes
    useEffect(() => {
      onDirtyChange?.(dirty);
    }, [dirty, onDirtyChange]);

    // Check if email already exists (debounced)
    useEffect(() => {
      const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const email = contact.email_address?.trim().toLowerCase();

      // Clear error and skip check if email is empty or invalid format
      if (!email || !EMAIL_REGEX.test(email)) {
        setEmailError(undefined);
        setEmailChecking(false);
        return;
      }

      setEmailChecking(true);
      const timeoutId = setTimeout(async () => {
        try {
          const { data, error } = await supabase.from("contacts").select("id").ilike("email_address", email).limit(1);

          if (error) {
            console.error("Error checking email:", error);
            setEmailError(undefined);
          } else if (data && data.length > 0) {
            setEmailError("Email already exists");
          } else {
            setEmailError(undefined);
          }
        } catch (err) {
          console.error("Error checking email:", err);
          setEmailError(undefined);
        } finally {
          setEmailChecking(false);
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    }, [contact.email_address]);

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
      updateFirstName: (name: string) => {
        setContact((c) => ({ ...c, first_name: name }));
      },
      updateLastName: (name: string) => {
        setContact((c) => ({ ...c, last_name: name }));
      },
      handleSave,
      handleReset,
    }));

    const updateContact = (updates: Partial<ContactForm>) => {
      setContact((c) => ({ ...c, ...updates }));
    };

    const updateMetaData = (updates: Partial<ContactForm["meta_data"]>) => {
      updateContact({
        meta_data: { ...contact.meta_data, ...updates },
      });
    };

    const handleSave = async () => {
      // Individual validation with toast messages like in projects
      if (!contact.first_name.trim()) {
        toast.showToast({
          title: "Missing First Name",
          message: "Please enter a first name for this contact",
          toastType: "warning",
        });
        return;
      }

      if (!contact.last_name.trim()) {
        toast.showToast({
          title: "Missing Last Name",
          message: "Please enter a last name for this contact",
          toastType: "warning",
        });
        return;
      }

      if (emailError) {
        toast.showToast({
          title: "Email Already Exists",
          message: "A contact with this email address already exists",
          toastType: "error",
        });
        return;
      }

      try {
        await onSave?.({ contact });
        baseline.current = JSON.stringify({ contact });
      } catch (error) {
        // Re-throw to parent for proper error handling
        throw error;
      }
    };

    const handleReset = () => {
      const nextContact: ContactForm = {
        first_name: initialContact?.first_name ?? "",
        last_name: initialContact?.last_name ?? "",
        email_address: initialContact?.email_address ?? "",
        phone_number: initialContact?.phone_number ?? "",
        meta_data: {
          preferred_name: initialContact?.meta_data?.preferred_name ?? "",
          notes: initialContact?.meta_data?.notes ?? "",
        },
        contact_clients: initialContact?.contact_clients ?? [],
      };
      setContact(nextContact);
      baseline.current = JSON.stringify({ contact: nextContact });
    };

    const handleClientsChange = (
      clients: { client: { id: string; name: string; status?: string }; note?: string }[],
    ) => {
      updateContact({ contact_clients: clients });
    };

    return (
      <View style={styles.container}>
        <ScrollView contentContainerStyle={{ gap: 8 }}>
          {/* Basic Information */}
          <View style={[styles.card, { borderColor: colors.border }]}>
            <ThemedText type="defaultSemiBold">Basic Information</ThemedText>
            <View style={{ gap: 8 }}>
              <ThemedInput
                testID="contact-preferred-name-input"
                label="Preferred Name"
                value={contact.meta_data.preferred_name}
                onChangeText={(text) => updateMetaData({ preferred_name: text })}
                placeholder="Enter preferred name"
              />
            </View>
          </View>

          {/* Contact Details */}
          <View style={[styles.card, { borderColor: colors.border }]}>
            <ThemedText type="defaultSemiBold">Contact Details</ThemedText>
            <View style={{ gap: 8 }}>
              <EmailInput
                label="Email Address"
                value={contact.email_address || ""}
                onChangeText={(text) => updateContact({ email_address: text })}
                placeholder="Enter email address"
                error={emailError}
                checking={emailChecking}
              />
              <PhoneInput
                label="Phone Number"
                value={contact.phone_number || ""}
                onChangeText={(text) => updateContact({ phone_number: text })}
                placeholder="Enter phone number"
              />
            </View>
          </View>

          {/* Clients */}
          <View style={[styles.card, { borderColor: colors.border }]}>
            <ClientsInput value={contact.contact_clients || []} state="editing" onChange={handleClientsChange} />
          </View>

          {/* Additional Information */}
          {/* <View style={[styles.card, { borderColor: colors.border }]}>
            <ThemedText type="defaultSemiBold">Additional Information</ThemedText>
            <View style={{ gap: 8 }}>
              <ThemedInput
                testID="contact-notes-input"
                label="Notes"
                value={contact.meta_data.notes}
                onChangeText={(text) => updateMetaData({ notes: text })}
                placeholder="Enter any additional notes"
                multiline
                numberOfLines={4}
                inputStyle={{ minHeight: 80 }}
              />
            </View>
          </View> */}
        </ScrollView>
      </View>
    );
  },
);

ContactCreateForm.displayName = "ContactCreateForm";

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  card: {
    gap: 8,
    padding: 12,
    borderRadius: 10,
    borderWidth: StyleSheet.hairlineWidth,
  },
});
